// src/pages/ReceiptPage.jsx
import React from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { formatStoredMoney } from "../utils/currency";
import {
  formatSplitTaxLabel,
  getUniformSplitGstRate,
  inferCombinedGstRate,
} from "../utils/gst";
import { resolveTaxLabelFromProfile } from "../utils/taxDetails";

const ReceiptPage = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { state } = useLocation();
  const order = state?.order;
  const storedProfile = JSON.parse(localStorage.getItem("userData") || "{}")?.data || {};
  const taxDisplayLabel = resolveTaxLabelFromProfile(storedProfile);
  const splitTax = taxDisplayLabel === "GST";

  if (!order) {
    return (
      <Container>
        <Message>Couldn’t load order #{orderId}</Message>
        <BackButton onClick={() => navigate(-1)}>← Go Back</BackButton>
      </Container>
    );
  }

  // Safely default lists
  const items = Array.isArray(order.itemslist) ? order.itemslist : [];
  const gstDetails = Array.isArray(order.gstdetails) ? order.gstdetails : [];
  const gstBreakup = Array.isArray(order.gstbreakup) ? order.gstbreakup : [];

  // Parse all monetary values as numbers
  const subTotal = parseFloat(order.totalamount) || 0;
  const cgst = parseFloat(gstDetails[0]?.cgst) || 0;
  const sgst = parseFloat(gstDetails[0]?.sgst) || 0;
  const gstSplitRate =
    getUniformSplitGstRate(gstBreakup) ||
    inferCombinedGstRate({
      taxable: subTotal,
      cgst,
      sgst,
    }) / 2;

  // Now this will always be a Number
  const grandTotal = (subTotal + cgst + sgst).toFixed(2);

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate(-1)}>← Back</BackButton>
        <OrderTitle>Order #{order.id}</OrderTitle>
      </Header>

      <InfoRow>
        <InfoItem>
          <Label>Table:</Label> {order.tableid}
        </InfoItem>
        <InfoItem>
          <Label>Date:</Label> {order.created_at}
        </InfoItem>
      </InfoRow>

      <SectionTitle>Items</SectionTitle>
      <Table>
        <thead>
          <tr>
            <Th>Product</Th>
            <Th center>Qty</Th>
            <Th right>Rate</Th>
            <Th right>Amount</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const rate = parseFloat(item.priceaftertax) || 0;
            const qty = parseFloat(item.qty) || 0;
            return (
              <tr key={item.productid}>
                <Td>{item.product_name}</Td>
                <Td center>{qty}</Td>
                <Td right>{formatStoredMoney(rate)}</Td>
                <Td right>{formatStoredMoney(rate * qty)}</Td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <Summary>
        <SummaryRow>
          <span>Subtotal:</span>
          <span>{formatStoredMoney(subTotal)}</span>
        </SummaryRow>

        {splitTax && cgst > 0 && (
          <SummaryRow>
            <span>{formatSplitTaxLabel("CGST", gstSplitRate)}:</span>
            <span>{formatStoredMoney(cgst)}</span>
          </SummaryRow>
        )}
        {splitTax && sgst > 0 && (
          <SummaryRow>
            <span>{formatSplitTaxLabel("SGST", gstSplitRate)}:</span>
            <span>{formatStoredMoney(sgst)}</span>
          </SummaryRow>
        )}
        {!splitTax && cgst + sgst > 0 && (
          <SummaryRow>
            <span>{taxDisplayLabel}:</span>
            <span>{formatStoredMoney(cgst + sgst)}</span>
          </SummaryRow>
        )}

        <Divider />

        <SummaryRow bold>
          <span>Total:</span>
          <span>{formatStoredMoney(grandTotal)}</span>
        </SummaryRow>
      </Summary>
    </Container>
  );
};

export default ReceiptPage;

/* Styled Components */

const Container = styled.div`
  max-width: 600px;
  margin: 32px auto;
  padding: 0 16px;
  font-family: sans-serif;
  color: #333;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #007bff;
  font-size: 1rem;
  cursor: pointer;
  margin-right: 16px;
`;

const OrderTitle = styled.h1`
  font-size: 1.4rem;
  margin: 0;
`;

const Message = styled.p`
  text-align: center;
  margin-top: 100px;
  font-size: 1.2rem;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
`;

const InfoItem = styled.div`
  font-size: 0.95rem;
`;

const Label = styled.span`
  font-weight: bold;
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  margin: 24px 0 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  thead {
    background: #f0f0f0;
  }
  th,
  td {
    padding: 8px 6px;
    border-bottom: 1px solid #ddd;
  }
`;

const Th = styled.th`
  text-align: left;
`;

const Td = styled.td`
  ${({ right }) => right && "text-align: right;"}
  ${({ center }) => center && "text-align: center;"}
`;

const Summary = styled.div`
  margin-top: 24px;
  font-size: 0.95rem;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 4px 0;
  ${({ bold }) => bold && "font-weight: bold;"}
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #ccc;
  margin: 12px 0;
`;
